'use client';

import { useState, useRef, useEffect } from 'react';
import { Slider } from 'src/shadcn/components/ui/slider';
import { Play, Pause, RotateCcw, Volume2 } from 'lucide-react';

interface SoundPlayerProps {
  audioSrc: string;
  title?: string;
}

export function CustomerCallSoundPlayer({
  audioSrc,
  title = 'Audio Track',
}: SoundPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => {
      setDuration(audio.duration);
      setCurrentTime(audio.currentTime);
    };

    const setAudioTime = () => setCurrentTime(audio.currentTime);

    audio.addEventListener('loadeddata', setAudioData);
    audio.addEventListener('timeupdate', setAudioTime);

    return () => {
      audio.removeEventListener('loadeddata', setAudioData);
      audio.removeEventListener('timeupdate', setAudioTime);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (audio) {
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const stopAudio = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleTimeChange = (newTime: number[]) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = newTime[0];
      setCurrentTime(newTime[0]);
    }
  };

  const handleVolumeChange = (newVolume: number[]) => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = newVolume[0];
      setVolume(newVolume[0]);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div>
      <audio ref={audioRef} src={audioSrc} />
      <div className='space-y-4'>
        <Slider
          value={[currentTime]}
          max={duration}
          step={1}
          onValueChange={handleTimeChange}
          className='w-full'
        />
        <div className='flex justify-between text-sm text-gray-500'>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
        <div className='flex items-center justify-between'>
          <button
            onClick={togglePlayPause}
            className='rounded-full bg-primary p-2 text-primary-foreground transition-colors hover:bg-primary/90'
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} />}
          </button>
          <button
            onClick={stopAudio}
            className='rounded-full bg-secondary p-2 text-secondary-foreground transition-colors hover:bg-secondary/90'
          >
            <RotateCcw size={24} />
          </button>
          <div className='flex items-center space-x-2'>
            <Volume2 size={20} />
            <Slider
              value={[volume]}
              max={1}
              step={0.01}
              onValueChange={handleVolumeChange}
              className='w-24'
            />
          </div>
        </div>
      </div>
    </div>
  );
}
